import { useEffect, useMemo } from 'react';

import { getFilterBy } from 'components/DataTable/paging';
import { BladeSide } from 'horizon/components/Damages/BladeDamagePlot/BladeDamagePlotContainer';
import {
  type AtlasGqlFilterBy,
  type AtlasGqlTableView,
  useBladeCardSummaryStatsQuery,
} from 'types/atlas-graphql';

const FIRST_PAGE_SIZE = 100;
const PAGE_SIZE = 1500;

type ViewAssetFilter = { key: string; value: [string] };

export type BladeCardViewFilters = {
  asset?: ViewAssetFilter[];
  'Blade Side'?: string[];
  Distance?: [[number, number]];
  Chord?: [[number, number]];
  [key: string]: unknown;
};

const getAssetValue = (filters: BladeCardViewFilters, key: string): string | undefined =>
  filters.asset?.find(f => f.key === key)?.value?.[0];

const parseBladeSide = (s?: string): BladeSide | undefined => {
  const allowed: BladeSide[] = [
    BladeSide.LeadingEdge,
    BladeSide.PressureSide,
    BladeSide.SuctionSide,
    BladeSide.TrailingEdge,
  ];
  return allowed.find(side => side === s);
};

export type BladeCardStats = {
  make?: string;
  model?: string;
  bladeSide?: BladeSide;
  distance?: [number, number];
  chord?: [number, number];
  totalCount: number;
  loadedCount: number;
  criticalCount: number;
  uniqueBlades: number;
  uniqueTurbines: number;
  isLoading: boolean;
  canRenderDiagram: boolean;
};

type UseBladeCardStatsOptions = {
  skip?: boolean;
};

// counts every matching Damage client-side: N Blade cards = N full Damage scans.
export const useBladeCardStats = (
  view: AtlasGqlTableView,
  { skip = false }: UseBladeCardStatsOptions = {}
): BladeCardStats => {
  const filters = view.filters as BladeCardViewFilters;
  const make = getAssetValue(filters, 'componentMake');
  const model = getAssetValue(filters, 'componentModel');
  const bladeSide = parseBladeSide(filters['Blade Side']?.[0]);
  const distance = filters.Distance?.[0];
  const chord = filters.Chord?.[0];

  const filterBy = useMemo(
    () => getFilterBy({ filters, columns: [] }).filter(Boolean) as AtlasGqlFilterBy[],
    [filters]
  );

  const { data, fetchMore } = useBladeCardSummaryStatsQuery({
    variables: {
      input: {},
      filterBy,
      limit: FIRST_PAGE_SIZE,
      offset: 0,
    },
    skip,
  });

  useEffect(() => {
    const items = data?.getHorizonDamages?.items;
    const totalCount = data?.getHorizonDamages?.totalCount ?? 0;
    if (items && items.length < totalCount) {
      fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: items.length,
        },
        updateQuery: (previousData, { fetchMoreResult }) => ({
          getHorizonDamages: {
            items: [
              ...(previousData.getHorizonDamages?.items ?? []),
              ...(fetchMoreResult.getHorizonDamages?.items ?? []),
            ],
            totalCount: previousData.getHorizonDamages?.totalCount,
          },
        }),
      });
    }
  }, [data, fetchMore]);

  const items = data?.getHorizonDamages?.items ?? [];
  const totalCount = data?.getHorizonDamages?.totalCount ?? 0;

  return {
    make,
    model,
    bladeSide,
    distance,
    chord,
    totalCount,
    loadedCount: items.length,
    criticalCount: items.filter(d => !!d?.primaryObservationGroupCritical).length,
    uniqueBlades: new Set(items.map(d => d?.asset?.id).filter(Boolean)).size,
    uniqueTurbines: new Set(
      items
        .map(d => d?.asset?.ancestors?.find(a => a?.assetType?.name === 'Turbine')?.id)
        .filter(Boolean)
    ).size,
    isLoading: !data || items.length < totalCount,
    canRenderDiagram: !!make && !!model && !!bladeSide && !!distance,
  };
};
